import { createFileRoute, Link, Outlet, useMatches } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Card, EmptyState, Spinner } from '@/components';
import { formatInt } from '@/lib/format';
import { apiJson } from '@/lib/api';

type GameItem = {
  id: number;
  slug: string;
  display_name: string;
  runs: number;
  players: number;
};

function GamesList() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'games'],
    queryFn: () => apiJson<GameItem[]>('/v1/admin/games'),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-red-600">
        {(error as Error).message}
        <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
          Retry
        </button>
      </p>
    );
  }
  if (!data || data.length === 0) {
    return <EmptyState title="No games in registry" />;
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Games</h1>
      <div className="grid gap-4 sm:grid-cols-2">
        {data.map((g) => (
          <Link
            key={g.id}
            to="/admin/games/$slug"
            params={{ slug: g.slug }}
            className={clsx(
              'group block rounded-[22px] focus:outline-none',
              'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
            )}
          >
            <Card
              className={clsx(
                'h-full transition',
                'group-hover:border-primary/20 group-hover:shadow-[0_14px_24px_rgba(0,0,0,0.08)]',
                'group-active:scale-[0.99]'
              )}
            >
              <h2 className="text-lg font-medium text-gray-900">{g.display_name}</h2>
              <p className="text-sm text-gray-500">{g.slug}</p>
              <p className="mt-2 text-sm text-gray-600">
                {formatInt(g.runs)} runs · {formatInt(g.players)} players
              </p>
              <p className="mt-4 text-sm font-medium text-primary group-hover:underline">
                Open game details →
              </p>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}

/**
 * Parent for `/admin/games` (list) and `/admin/games/$slug` (detail).
 * We render the list here when the active leaf is this layout; the index file
 * route (`/admin/games/`) did not match `/admin/games` from the tab link, so an
 * `<Outlet />`-only layout showed a blank page.
 */
function GamesLayout() {
  const matches = useMatches();
  const leafId = matches.at(-1)?.routeId;
  if (leafId === '/admin/games/$slug') {
    return <Outlet />;
  }
  return <GamesList />;
}

export const Route = createFileRoute('/admin/games')({ component: GamesLayout });
